import { topUpJade, type TopUpPack, type TopUpReceipt } from '@nine/shared';
import { PAY_METHODS, type PayMethod, type PaymentGateway } from '../payment';
import { $, closeButton, h } from './dom';

const fmt = (n: number) => n.toLocaleString('th-TH');

/** The payment window over the menu: pick a method, pay, then the receipt is credited. */
export class Checkout {
  private readonly root = h('div', { id: 'checkout', hidden: true, role: 'dialog', ariaModal: 'true' });
  private busy = false;

  constructor(
    private readonly gateway: PaymentGateway,
    /** Credits the receipt to the character; false when it was refused. */
    private readonly credit: (r: TopUpReceipt) => boolean,
  ) {
    $('#panel').append(this.root);
    this.root.addEventListener('pointerdown', (e) => e.stopPropagation());
  }

  get isOpen(): boolean {
    return !this.root.hidden;
  }

  close(): void {
    if (this.busy) return;
    this.root.hidden = true;
    this.root.replaceChildren();
  }

  open(pack: TopUpPack, first: boolean): void {
    let method: PayMethod = 'promptpay';
    const jade = topUpJade(pack, first);
    const methods = PAY_METHODS.map(([id, label]) => {
      const b = h(
        'button',
        { type: 'button', class: 'pay-method', ariaPressed: String(id === method) },
        label,
      );
      b.addEventListener('click', () => {
        method = id;
        for (const m of methods) m.ariaPressed = String(m === b);
      });
      return b;
    });
    const status = h('p', { class: 'note', role: 'status' });
    const cancel = h('button', { type: 'button', class: 'btn ghost' }, 'ยกเลิก');
    const pay = h('button', { type: 'button', class: 'btn' }, `ชำระ ${fmt(pack.baht)} บาท`);
    cancel.addEventListener('click', () => this.close());
    pay.addEventListener('click', () => {
      this.busy = true;
      pay.disabled = cancel.disabled = true;
      for (const m of methods) m.disabled = true;
      status.textContent = 'กำลังชำระเงิน…';
      this.gateway
        .pay(pack, method)
        .then((r) => {
          const ok = this.credit(r);
          status.textContent = ok
            ? `ชำระสำเร็จ ได้รับหยกเซียน ${fmt(jade)} · เลขที่ ${r.id}`
            : 'รายการนี้ถูกบันทึกไปแล้ว';
        })
        .catch(() => (status.textContent = 'ชำระเงินไม่สำเร็จ ยังไม่มีการตัดเงิน ลองใหม่อีกครั้ง'))
        .finally(() => {
          this.busy = false;
          cancel.disabled = false;
          cancel.textContent = 'ปิด';
          pay.hidden = true;
        });
    });
    this.root.replaceChildren(
      h(
        'div',
        { class: 'checkout-box' },
        h(
          'header',
          { class: 'win-head' },
          h('h2', { class: 'win-title' }, 'เติมหยกเซียน'),
          closeButton(() => this.close()),
        ),
        h(
          'div',
          { class: 'checkout-sum' },
          h('b', {}, `${fmt(jade)} หยก`),
          h('span', {}, `${fmt(pack.baht)} บาท`),
        ),
        h(
          'p',
          { class: 'note' },
          `หยก ${fmt(pack.jade)}` +
            (pack.bonus ? ` + โบนัส ${fmt(pack.bonus)}` : '') +
            (first ? ` + เติมครั้งแรก ${fmt(pack.jade)}` : ''),
        ),
        h('div', { class: 'pay-methods' }, ...methods),
        this.gateway.sandbox
          ? h(
              'p',
              { class: 'checkout-sandbox' },
              'โหมดทดสอบ: ยังไม่มีการตัดเงินจริง ระบบชำระเงินจริงจะเปิดเมื่อมีเซิร์ฟเวอร์',
            )
          : null,
        status,
        h('div', { class: 'dlg-actions' }, cancel, pay),
      ),
    );
    this.root.hidden = false;
    pay.focus();
  }
}
